import { useEffect, useRef, useState } from "react";
import { X, Music2, Film, Download, Copy, Scissors } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const CLIP_LEN = 30;
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

/**
 * 30-second shareable clip generator. Lets the listener:
 *  - scrub a playback head against the source MP3 to pick a 30s window
 *  - generate either an audio MP3 or a waveform MP4
 *  - download the file + copy a shareable link
 */
export default function ClipShareModal({ ep, open, onClose }) {
  const audioRef = useRef(null);
  const [duration, setDuration] = useState(0);
  const [start, setStart] = useState(0);
  const [previewTime, setPreviewTime] = useState(0);
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const [format, setFormat] = useState("audio");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null); // { url, format }

  useEffect(() => {
    if (!open) {
      setResult(null);
      setStart(0);
      setPreviewPlaying(false);
      if (audioRef.current) audioRef.current.pause();
    }
  }, [open]);

  useEffect(() => {
    if (!open || !ep?.audio_url) return;
    const a = audioRef.current;
    if (!a) return;
    a.src = ep.audio_url.startsWith("http") ? ep.audio_url : `${BACKEND}${ep.audio_url}`;
    const onMeta = () => setDuration(a.duration || 0);
    const onTime = () => setPreviewTime(a.currentTime);
    const onEnd = () => setPreviewPlaying(false);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("ended", onEnd);
      a.pause();
    };
  }, [open, ep?.audio_url]);

  // Auto-stop preview once we've played 30s past `start`
  useEffect(() => {
    if (!previewPlaying) return;
    const a = audioRef.current;
    if (a && a.currentTime > start + CLIP_LEN) {
      a.pause();
      setPreviewPlaying(false);
    }
  }, [previewTime, previewPlaying, start]);

  if (!open || !ep) return null;

  const maxStart = Math.max(0, duration - CLIP_LEN);
  const fmtTime = (s) => `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, "0")}`;

  const previewToggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (previewPlaying) {
      a.pause();
      setPreviewPlaying(false);
    } else {
      a.currentTime = start;
      a.play().catch(() => {});
      setPreviewPlaying(true);
    }
  };

  const generate = async () => {
    setGenerating(true);
    setResult(null);
    const t = toast.loading(format === "video" ? "rendering waveform video..." : "slicing your clip...");
    try {
      const { data } = await api.post(`/episodes/${ep.number}/clip`, {
        start_seconds: start, format, duration: CLIP_LEN,
      });
      const url = data.url.startsWith("http") ? data.url : `${BACKEND}${data.url}`;
      setResult({ url, format });
      toast.success("clip ready ↓", { id: t });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "clip failed", { id: t });
    } finally { setGenerating(false); }
  };

  const copyLink = async () => {
    if (!result?.url) return;
    try {
      await navigator.clipboard.writeText(result.url);
      toast.success("link copied");
    } catch {
      toast.error("could not copy");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
      onClick={onClose}
      data-testid="clip-modal-backdrop"
    >
      <div
        className="relative w-full max-w-xl bg-coal border border-hotpink/60 p-6 md:p-8 shadow-[6px_6px_0px_#FF007F]"
        onClick={(e) => e.stopPropagation()}
        data-testid="clip-modal"
      >
        <button onClick={onClose} className="absolute top-3 right-3 text-white/50 hover:text-hotpink" data-testid="clip-modal-close">
          <X size={20} />
        </button>
        <div className="flex items-center gap-2 mb-1">
          <Scissors size={16} className="text-hotpink" />
          <p className="scribble text-hotpink text-2xl rotate-[-1deg] inline-block">share a 30s clip</p>
        </div>
        <p className="brush-title text-2xl text-white leading-tight mb-1">EP {String(ep.number).padStart(2, "0")} — {ep.title}</p>
        <p className="font-outfit text-white/60 text-sm mb-5">Pick where the 30 seconds starts. We&rsquo;ll generate an audio file or a waveform video you can post anywhere.</p>

        <audio ref={audioRef} preload="metadata" className="hidden" />

        <div className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <span className="font-outfit text-xs uppercase tracking-widest text-white/50">Start at</span>
            <span className="font-outfit text-xs text-hotpink" data-testid="clip-start-label">
              {fmtTime(start)} → {fmtTime(start + CLIP_LEN)}
            </span>
          </div>
          <input
            type="range"
            min={0}
            max={maxStart || 1}
            step={1}
            value={start}
            onChange={(e) => setStart(Number(e.target.value))}
            data-testid="clip-start-slider"
            className="w-full accent-hotpink"
            disabled={!duration}
          />
          <div className="flex items-center justify-between mt-1 text-[10px] uppercase tracking-widest text-white/30">
            <span>0:00</span>
            <span>{fmtTime(duration || 0)}</span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-5">
          <button
            type="button"
            onClick={previewToggle}
            disabled={!duration}
            data-testid="clip-preview-btn"
            className="bg-white text-ink hover:bg-hotpink hover:text-white font-outfit font-extrabold uppercase tracking-widest text-[10px] px-4 py-2 transition-colors disabled:opacity-40"
          >
            {previewPlaying ? "stop preview" : "preview 30s"}
          </button>
          <div className="flex items-center gap-2" role="radiogroup" aria-label="Clip format">
            <button
              type="button"
              onClick={() => setFormat("audio")}
              data-testid="clip-format-audio"
              className={`flex items-center gap-1 px-3 py-2 font-outfit font-extrabold uppercase tracking-widest text-[10px] transition-colors ${format === "audio" ? "bg-hotpink text-white" : "border border-white/15 text-white/70 hover:text-hotpink"}`}
            >
              <Music2 size={12} /> MP3
            </button>
            <button
              type="button"
              onClick={() => setFormat("video")}
              data-testid="clip-format-video"
              className={`flex items-center gap-1 px-3 py-2 font-outfit font-extrabold uppercase tracking-widest text-[10px] transition-colors ${format === "video" ? "bg-hotpink text-white" : "border border-white/15 text-white/70 hover:text-hotpink"}`}
            >
              <Film size={12} /> MP4 waveform
            </button>
          </div>
        </div>

        <button
          onClick={generate}
          disabled={generating || !duration}
          data-testid="clip-generate-btn"
          className="w-full bg-hotpink text-white hover:bg-white hover:text-ink font-outfit font-extrabold uppercase tracking-widest text-xs px-5 py-3 shadow-[3px_3px_0px_#FFFFFF] hover:shadow-[6px_6px_0px_#FF007F] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {generating ? (format === "video" ? "rendering..." : "slicing...") : "generate clip"}
        </button>

        {result && (
          <div className="mt-5 bg-ink border border-hotpink/40 p-4" data-testid="clip-result">
            <p className="font-outfit text-white/60 text-xs uppercase tracking-widest mb-3">your clip is ready</p>
            {result.format === "audio" ? (
              <audio controls src={result.url} className="w-full mb-3" data-testid="clip-audio-preview" />
            ) : (
              <video controls src={result.url} className="w-full max-h-72 bg-black mb-3" data-testid="clip-video-preview" />
            )}
            <div className="flex flex-wrap gap-2">
              <a
                href={result.url}
                download
                data-testid="clip-download-btn"
                className="inline-flex items-center gap-1 bg-white text-ink hover:bg-hotpink hover:text-white font-outfit font-extrabold uppercase tracking-widest text-[10px] px-4 py-2 transition-colors"
              >
                <Download size={12} /> download
              </a>
              <button
                onClick={copyLink}
                data-testid="clip-copy-link"
                className="inline-flex items-center gap-1 border border-white/15 text-white/70 hover:text-hotpink font-outfit font-extrabold uppercase tracking-widest text-[10px] px-4 py-2 transition-colors"
              >
                <Copy size={12} /> copy link
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
